import React from 'react';
import { Tooltip } from 'devextreme-react';

interface LabRefChipProps {
  labRefId: number | null | undefined;
  labRefData: Array<{
    Lr_Id: number;
    Lr_Nombre: string;
    Lr_Color?: string | null;
  }>;
  size?: 'small' | 'medium' | 'large';
  opacity?: number;
  onClick?: () => void;
  clickable?: boolean;
  /** Laboratorio al que el estudio se remite por una remisión temporal vigente */
  temporalLabRefId?: number | null;
  /** Fin de la vigencia de la remisión temporal */
  temporalEndDate?: string | Date | null;
  /** La remisión temporal no se aplicará porque el estudio ya trae laboratorio de la BD */
  temporalIgnored?: boolean;
}

const LabRefChip: React.FC<LabRefChipProps> = ({
  labRefId,
  labRefData,
  size = 'small',
  opacity = 0.9,
  onClick,
  clickable = false,
  temporalLabRefId = null,
  temporalEndDate = null,
  temporalIgnored = false
}) => {
  const labRef = labRefData.find(lab => lab.Lr_Id === labRefId);
  const temporalLabRef = temporalLabRefId != null ? labRefData.find(lab => lab.Lr_Id === temporalLabRefId) : undefined;

  // El chip se parte solo si la remisión temporal realmente va a aplicarse; si el estudio
  // ya tiene otro laboratorio guardado, ese tiene prioridad y la remisión se descarta
  const isTemporal = !!temporalLabRef && !temporalIgnored;
  const isTemporalIgnored = !!temporalLabRef && temporalIgnored;

  // Determinar si hay laboratorio de referencia o no
  const hasLabRef = isTemporal ? true : labRef && labRef.Lr_Nombre;
  const labName = isTemporal ? temporalLabRef.Lr_Nombre : hasLabRef ? labRef!.Lr_Nombre : 'INTERNO';

  // En remisión temporal el chip se parte: mitad izquierda el laboratorio habitual del
  // estudio (blanco si no tiene) y mitad derecha el laboratorio temporal, cuyo nombre
  // es el que se muestra.
  const originalColor = labRef?.Lr_Color || '#ffffff';
  const temporalColor = temporalLabRef?.Lr_Color || '#e9ecef';
  const labColor = isTemporal ? temporalColor : hasLabRef ? labRef?.Lr_Color || '#e9ecef' : '#f8f9fa'; // Color más claro para interno

  // Función para determinar el color del texto basado en el brillo del fondo
  const getTextColor = (backgroundColor: string) => {
    // Convertir hex a RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calcular brillo usando la fórmula de luminosidad
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // Si es claro usar texto oscuro, si es oscuro usar texto claro
    return brightness > 128 ? '#495057' : '#ffffff';
  };

  // Función para crear un color de borde sutil
  const getBorderColor = (color: string) => {
    if (color === '#e9ecef') {
      return '#ced4da'; // Borde ligeramente más oscuro para el gris por defecto
    }
    
    // Para otros colores, crear un borde más sutil (solo 10% más oscuro)
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * 10); // 10% en lugar de 20%
    const R = Math.max(0, (num >> 16) - amt);
    const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
    const B = Math.max(0, (num & 0x0000FF) - amt);
    return "#" + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
  };

  // Configuraciones de tamaño
  const sizeConfig = {
    small: {
      fontSize: '9px',
      padding: '1px 8px',
      borderRadius: '16px',
      minWidth: '40px',
      borderWidth: '1px'
    },
    medium: {
      fontSize: '11px',
      padding: '3px 10px',
      borderRadius: '18px',
      minWidth: '50px',
      borderWidth: '1px'
    },
    large: {
      fontSize: '13px',
      padding: '4px 12px',
      borderRadius: '20px',
      minWidth: '60px',
      borderWidth: '1px'
    }
  };

  const currentSize = sizeConfig[size];
  const textColor = getTextColor(labColor);
  const borderColor = getBorderColor(labColor);

  // Generar ID único para el tooltip
  const chipId = `lab-ref-chip-${labRefId}-${Math.random().toString(36).substr(2, 9)}`;

  const formatEndDate = (date: string | Date) => {
    const value = new Date(date);
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${pad(value.getDate())}/${pad(value.getMonth() + 1)}/${value.getFullYear()} ${pad(value.getHours())}:${pad(value.getMinutes())}`;
  };

  // Contenido del tooltip (DevExtreme) según el caso del chip
  const renderTooltipContent = () => {
    if (isTemporal) {
      const origen = labRef?.Lr_Nombre || 'INTERNO';
      return (
        <div className='tooltip-content'>
          <div className='tooltip-title'>
            <strong>Remisión temporal</strong>
          </div>
          <div className='tooltip-item'>
            {origen} → {labName}
          </div>
          {temporalEndDate && <div className='tooltip-item'>Vigente hasta el {formatEndDate(temporalEndDate)}</div>}
        </div>
      );
    }

    if (isTemporalIgnored) {
      return (
        <div className='tooltip-content'>
          <div className='tooltip-title'>
            <strong>Remisión temporal no aplicada</strong>
          </div>
          <div className='tooltip-item'>El estudio ya tiene asignado {labName}, que tiene prioridad.</div>
          <div className='tooltip-item'>La remisión hacia {temporalLabRef?.Lr_Nombre} no se tendrá en cuenta.</div>
          {temporalEndDate && <div className='tooltip-item'>Vigente hasta el {formatEndDate(temporalEndDate)}</div>}
        </div>
      );
    }

    return <div className='tooltip-content'>{hasLabRef ? `Examen procesado por ${labName}` : 'Examen procesado internamente'}</div>;
  };

  const chipStyle = {
    display: 'inline-block',
    backgroundColor: labColor,
    // Mitad izquierda: laboratorio habitual; mitad derecha: laboratorio temporal
    backgroundImage: isTemporal ? `linear-gradient(90deg, ${originalColor} 0 50%, ${temporalColor} 50% 100%)` : undefined,
    border: `${currentSize.borderWidth} solid ${borderColor}`,
    color: textColor,
    // El texto cruza las dos mitades, así que necesita contraste en ambas
    textShadow: isTemporal ? (textColor === '#ffffff' ? '0 0 2px rgba(0,0,0,0.65)' : '0 0 2px rgba(255,255,255,0.85)') : undefined,
    fontSize: currentSize.fontSize,
    fontWeight: isTemporal ? '600' : hasLabRef ? '500' : ('400' as const),
    padding: currentSize.padding,
    borderRadius: currentSize.borderRadius,
    width: '65px',
    maxWidth: '65px',
    textAlign: 'center' as const,
    boxShadow: hasLabRef ? '0 1px 3px rgba(0, 0, 0, 0.05)' : '0 1px 2px rgba(0, 0, 0, 0.03)',
    transition: 'all 0.2s ease',
    letterSpacing: '0.25px',
    opacity: hasLabRef ? 1 : 0.8,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
    boxSizing: 'border-box' as const,
    lineHeight: '1.4',
    verticalAlign: 'middle' as const,
    cursor: clickable ? 'pointer' as const : 'default' as const,
  };
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (clickable && onClick) {
      e.stopPropagation();
      e.preventDefault();
      onClick();
    }
  };

  return (
    <>
      {/* El contenedor relativo permite anclar el punto de aviso a la esquina del chip */}
      <div id={chipId} style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle', cursor: clickable ? 'pointer' : 'default' }} onClick={handleClick}>
        <div style={chipStyle}>{labName}</div>
        {isTemporalIgnored && (
          <span
            style={{
              position: 'absolute',
              top: '-2px',
              left: '-2px',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#d32f2f',
              border: '1px solid #ffffff',
              boxSizing: 'border-box',
            }}
          />
        )}
      </div>
      <Tooltip
        target={`#${chipId}`}
        showEvent='mouseenter'
        hideEvent='mouseleave'
        position='top'
      >
        {renderTooltipContent()}
      </Tooltip>
    </>
  );
};

export default LabRefChip;
